import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { SortDirection } from './../enums/SortDirection';
import type { EntityState } from './../enums/EntityState';
import type { BibAssignPlaceUnseededType } from './../enums/BibAssignPlaceUnseededType';
import type { BibAssignSortType } from './../enums/BibAssignSortType';

export class BibAssignSort extends BaseObj {
    public ID: number;
    public __id: number;

    public Direction: SortDirection;
    public __direction: SortDirection;

    public EventID: number;
    public __eventid: number;

    public PlaceUnseededLast: BibAssignPlaceUnseededType;
    public __placeunseededlast: BibAssignPlaceUnseededType;

    public ResultID: number | null;
    public __resultid: number | null;

    public SeedingListID: number | null;
    public __seedinglistid: number | null;

    public SeriesID: number | null;
    public __seriesid: number | null;

    public Sort: number;
    public __sort: number;

    public SortType: BibAssignSortType;
    public __sorttype: BibAssignSortType;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Direction?: SortDirection;
        EntityState?: EntityState;
        EventID?: number;
        PlaceUnseededLast?: BibAssignPlaceUnseededType;
        ResultID?: number | null;
        SeedingListID?: number | null;
        SeriesID?: number | null;
        Sort?: number;
        SortType?: BibAssignSortType;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<BibAssignSort>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('BibAssignSort tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Direction = values.Direction ?? 'Ascending';
        this.__direction = values.Direction ?? 'Ascending';
        Object.defineProperty(this, 'Direction', {
            get(): SortDirection {
                return this.__direction;
            },
            set(value: SortDirection) {
                this.__entityStateHandler.propertyChanged('Direction', this.__direction, value);
                this.__direction = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PlaceUnseededLast = values.PlaceUnseededLast ?? 'Last';
        this.__placeunseededlast = values.PlaceUnseededLast ?? 'Last';
        Object.defineProperty(this, 'PlaceUnseededLast', {
            get(): BibAssignPlaceUnseededType {
                return this.__placeunseededlast;
            },
            set(value: BibAssignPlaceUnseededType) {
                this.__entityStateHandler.propertyChanged('PlaceUnseededLast', this.__placeunseededlast, value);
                this.__placeunseededlast = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ResultID = values.ResultID ?? null;
        this.__resultid = values.ResultID ?? null;
        Object.defineProperty(this, 'ResultID', {
            get(): number | null {
                return this.__resultid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ResultID', this.__resultid, value);
                this.__resultid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SeedingListID = values.SeedingListID ?? null;
        this.__seedinglistid = values.SeedingListID ?? null;
        Object.defineProperty(this, 'SeedingListID', {
            get(): number | null {
                return this.__seedinglistid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SeedingListID', this.__seedinglistid, value);
                this.__seedinglistid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SeriesID = values.SeriesID ?? null;
        this.__seriesid = values.SeriesID ?? null;
        Object.defineProperty(this, 'SeriesID', {
            get(): number | null {
                return this.__seriesid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SeriesID', this.__seriesid, value);
                this.__seriesid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sort = values.Sort ?? 0;
        this.__sort = values.Sort ?? 0;
        Object.defineProperty(this, 'Sort', {
            get(): number {
                return this.__sort;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Sort', this.__sort, value);
                this.__sort = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SortType = values.SortType ?? 'LastName';
        this.__sorttype = values.SortType ?? 'LastName';
        Object.defineProperty(this, 'SortType', {
            get(): BibAssignSortType {
                return this.__sorttype;
            },
            set(value: BibAssignSortType) {
                this.__entityStateHandler.propertyChanged('SortType', this.__sorttype, value);
                this.__sorttype = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Direction": ${this.Direction == 'Ascending' ? 0 : this.Direction == 'Descending' ? 1 : 0}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"PlaceUnseededLast": ${this.PlaceUnseededLast == 'Last' ? 0 : this.PlaceUnseededLast == 'First' ? 1 : 0}` +
            ',' +
            `"ResultID": ${JSON.stringify(this.ResultID)}` +
            ',' +
            `"SeedingListID": ${JSON.stringify(this.SeedingListID)}` +
            ',' +
            `"SeriesID": ${JSON.stringify(this.SeriesID)}` +
            ',' +
            `"Sort": ${JSON.stringify(this.Sort)}` +
            ',' +
            `"SortType": ${this.SortType == 'LastName' ? 0 : this.SortType == 'FirstName' ? 1 : this.SortType == 'SeedingList' ? 2 : this.SortType == 'Club' ? 3 : this.SortType == 'Random' ? 4 : this.SortType == 'CompetitorSeeding' ? 5 : this.SortType == 'Series' ? 6 : this.SortType == 'Result' ? 7 : this.SortType == 'ClubTeam' ? 8 : this.SortType == 'Team' ? 9 : this.SortType == 'CategorySort' ? 10 : this.SortType == 'GroupSort' ? 11 : this.SortType == 'CompetitorSeedingGroup' ? 12 : this.SortType == 'SignupTime' ? 13 : this.SortType == 'Birthdate' ? 14 : this.SortType == 'Gender' ? 15 : this.SortType == 'QueueNumber' ? 16 : 0}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of BibAssignSort from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Direction?: SortDirection;
        EventID?: number;
        PlaceUnseededLast?: BibAssignPlaceUnseededType;
        ResultID?: number | null;
        SeedingListID?: number | null;
        SeriesID?: number | null;
        Sort?: number;
        SortType?: BibAssignSortType;
        EntityState?: 'Added' | 'Unchanged';
    }): BibAssignSort {
        return new BibAssignSort(values);
    }

    public static _FromServerJson(json: any): BibAssignSort {
        return new BibAssignSort({
            ID: json.ID,
            Direction: json.Direction == 0 ? 'Ascending' : json.Direction == 1 ? 'Descending' : 'Ascending',
            EventID: json.EventID,
            PlaceUnseededLast: json.PlaceUnseededLast == 0 ? 'Last' : json.PlaceUnseededLast == 1 ? 'First' : 'Last',
            ResultID: json.ResultID ?? null,
            SeedingListID: json.SeedingListID ?? null,
            SeriesID: json.SeriesID ?? null,
            Sort: json.Sort,
            SortType:
                json.SortType == 0
                    ? 'LastName'
                    : json.SortType == 1
                      ? 'FirstName'
                      : json.SortType == 2
                        ? 'SeedingList'
                        : json.SortType == 3
                          ? 'Club'
                          : json.SortType == 4
                            ? 'Random'
                            : json.SortType == 5
                              ? 'CompetitorSeeding'
                              : json.SortType == 6
                                ? 'Series'
                                : json.SortType == 7
                                  ? 'Result'
                                  : json.SortType == 8
                                    ? 'ClubTeam'
                                    : json.SortType == 9
                                      ? 'Team'
                                      : json.SortType == 10
                                        ? 'CategorySort'
                                        : json.SortType == 11
                                          ? 'GroupSort'
                                          : json.SortType == 12
                                            ? 'CompetitorSeedingGroup'
                                            : json.SortType == 13
                                              ? 'SignupTime'
                                              : json.SortType == 14
                                                ? 'Birthdate'
                                                : json.SortType == 15
                                                  ? 'Gender'
                                                  : json.SortType == 16
                                                    ? 'QueueNumber'
                                                    : 'LastName',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of BibAssignSort from another instance of BibAssignSort.
     * @param other The BibAssignSort you want to clone
     * @returns A cloned BibAssignSort
     */
    public static FromOther(other: BibAssignSort): BibAssignSort {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return BibAssignSort._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return BibAssignSort.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: ('ID' | 'Direction' | 'EventID' | 'PlaceUnseededLast' | 'ResultID' | 'SeedingListID' | 'SeriesID' | 'Sort' | 'SortType')[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'BibAssignSort';
}
